import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { VideoPlayer } from "@/components/VideoPlayer";
import { VideoAdPlaceholder } from "@/components/VideoAdPlaceholder";
import { AdBanner } from "@/components/AdBanner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Star, Clock, Globe } from "lucide-react";

const Watch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [movie, setMovie] = useState(null);
  const [error, setError] = useState(false);
  const [adDone, setAdDone] = useState(false);

  const isSubscribed = !!(user && user.subscribed);
  const showAd = !isSubscribed && !adDone;

  useEffect(() => {
    window.scrollTo(0, 0);
    setAdDone(false);
    api.get(`/movies/${id}`)
      .then((r) => setMovie(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-white/70">This title could not be found.</p>
        <Link to="/browse" className="rounded-full bg-[#00F0FF] text-black font-semibold px-6 py-2">Back to Browse</Link>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="watch-page">
      <div className="pt-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <button onClick={() => navigate(-1)} data-testid="watch-back" className="flex items-center gap-2 text-white/80 hover:text-[#00F0FF] transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
          </div>
          {showAd ? (
            <VideoAdPlaceholder onFinish={() => setAdDone(true)} />
          ) : (
            <VideoPlayer src={movie.stream_url} type={movie.stream_type} fallback={movie.stream_url_fallback} poster={movie.backdrop_url} title={movie.title} />
          )}
        </div>

        {!isSubscribed && <AdBanner className="max-w-6xl mx-auto mt-4" />}

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white">{movie.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className="font-mono-ui text-[10px] tracking-widest px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF]">{movie.quality}</span>
            <span className="flex items-center gap-1 text-sm text-white/70"><Star size={14} className="text-[#00F0FF]" fill="currentColor" />{movie.rating?.toFixed(1)}</span>
            <span className="text-sm text-white/70">{movie.release_year}</span>
            <span className="flex items-center gap-1 text-sm text-white/70"><Clock size={14} />{movie.duration}</span>
            <span className="flex items-center gap-1 text-sm text-white/70"><Globe size={14} />{movie.origin}</span>
          </div>
          <p className="mt-4 text-sm md:text-base text-white/60 max-w-3xl leading-relaxed">{movie.description}</p>
        </div>
      </div>
    </div>
  );
};

export default Watch;
